/**
 * API: oportunidades perdidas (Sprint 6 + Sprint 16 v2 playbook).
 *
 * Reporte de convocatorias cerradas que hubieran calzado (matching
 * retroactivo) + patterns por sector + peer captures + acciones del
 * usuario (no_aplicaba / save_template).
 */

import { apiErrorFromResponse, safeFetch } from "../errors";
import { supabase } from "../supabase";
import { API_V1, getAccessToken, request } from "./_client";

export interface LostOpportunityItemDTO {
  opportunity_id: string;
  title: string;
  sector: string | null;
  source_name: string;
  closes_at: string;
  amount_total_cop: string | null;
  amount_per_project_max_cop: string | null;
  match_score: number;
  match_reasons: string[];
  // `saved_as_template` sigue en la respuesta, pero la pantalla ya no lo lee:
  // "Guardar template" salio en #712.
}

export interface LostPatternDTO {
  sector: string;
  count: number;
  total_per_project_cop: string;
  causa: "ventana_corta" | "sector_debil" | "concentracion_temporal" | "mixto";
  causa_confidence: number;
  opportunity_ids: string[];
}

export interface PeerCaptureDTO {
  dane_code: string;
  name: string;
  amount_cop: string | null;
}

export interface LostOpportunitiesReportDTO {
  dane_code: string;
  name: string;
  months_window: number;
  min_score: number;
  items: LostOpportunityItemDTO[];
  count: number;
  total_amount_per_project_cop: string;
  // Sprint 16 — opcionales según query params
  patterns: LostPatternDTO[];
  peer_captures_by_opp: Record<string, PeerCaptureDTO[]>;
  estimated_peer_captured_cop: string;
}

export interface FetchLostOpportunitiesOptions {
  months?: number;
  withPatterns?: boolean;
  withPeerCaptures?: boolean;
}

/**
 * Sprint 18 — H-1 (auditoría seguridad mayo 19, 2026):
 * el endpoint ahora deriva el `tenant_id` del JWT del usuario vía
 * `ensure_context`. El cliente ya no envía `tenant_id` — eso prevenía
 * que un atacante autenticado enumerara tenants ajenos.
 *
 * Si el usuario NO es miembro del muni indicado por `dane`, el backend
 * devuelve 403 (`ApiError.status === 403`).
 */
export function fetchLostOpportunities(
  dane: string,
  optionsOrMonths: number | FetchLostOpportunitiesOptions = 24,
): Promise<LostOpportunitiesReportDTO> {
  const options: FetchLostOpportunitiesOptions =
    typeof optionsOrMonths === "number" ? { months: optionsOrMonths } : optionsOrMonths;
  const qs = new URLSearchParams();
  qs.set("months", String(options.months ?? 24));
  if (options.withPatterns) qs.set("with_patterns", "true");
  if (options.withPeerCaptures) qs.set("with_peer_captures", "true");
  return request<LostOpportunitiesReportDTO>(
    `/municipalities/${encodeURIComponent(dane)}/lost-opportunities?${qs.toString()}`,
  );
}

/**
 * Marca una opp perdida como "No aplicaba" (Sprint 16). El backend acepta
 * tambien `save_template`, pero la pantalla ya no lo ofrece (#712).
 */
export async function postLostOpportunityAction(args: {
  opportunityId: string;
  municipality: string;
  action: "not_applicable";
}): Promise<void> {
  const token = await getAccessToken();
  const headers = new Headers();
  headers.set("Accept", "application/json");
  headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await safeFetch(
    `${API_V1}/lost-opportunities/${encodeURIComponent(args.opportunityId)}/action`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: args.action,
        municipality: args.municipality,
      }),
    },
  );
  if (res.status === 401) {
    await supabase.auth.signOut();
  }
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }
}

/**
 * Deshace "No aplicaba": la convocatoria vuelve al reporte y a los patrones
 * (#712). Idempotente en el backend: dos clics no dan error.
 */
export async function deleteLostOpportunityAction(args: {
  opportunityId: string;
  municipality: string;
  action: "not_applicable";
}): Promise<void> {
  const token = await getAccessToken();
  const headers = new Headers();
  headers.set("Accept", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const qs = new URLSearchParams({ action: args.action, municipality: args.municipality });
  const res = await safeFetch(
    `${API_V1}/lost-opportunities/${encodeURIComponent(args.opportunityId)}/action?${qs.toString()}`,
    { method: "DELETE", headers },
  );
  if (res.status === 401) {
    await supabase.auth.signOut();
  }
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }
}
